import { ResultOf, VariablesOf, gql } from '@/utilities/gql';
import {
  infiniteQueryOptions,
  useSuspenseInfiniteQuery,
} from '@tanstack/react-query';
import { NARRATIVE_CARD_FRAGMENT } from '@/components/NarrativeCard';
import { gqlClient } from '@/utilities/graphQLClient';
import { getOperationName } from '@/utilities/gqlQuery/query';

const QUERY = gql(
  `
  query NarrativesCommunityInfinite($first: Int, $last: Int, $before: String, $after: String) {
    narratives(first: $first, last: $last, before: $before, after: $after) {
      edges {
        cursor
        node {
          id
          ...NarrativeCardFields
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`,
  [NARRATIVE_CARD_FRAGMENT]
);

type Variables = VariablesOf<typeof QUERY>;
export type Result = ResultOf<typeof QUERY>;
const operationName = getOperationName(QUERY)!;

export const useNarrativesCommunityInfinite = (variables: Variables) => {
  return useSuspenseInfiniteQuery({
    ...useNarrativesCommunityInfinite.getOptions(variables),
    staleTime: Infinity,
  });
};

useNarrativesCommunityInfinite.getOptions = (
  variables: VariablesOf<typeof QUERY>
) =>
  infiniteQueryOptions({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: operationName,
    initialPageParam: { after: variables.after, before: variables.before },
    queryFn: ({ pageParam, direction }) => {
      return gqlClient.request(
        QUERY,
        direction === 'backward' || pageParam.before
          ? {
              last: variables?.last,
              before: pageParam.before,
            }
          : {
              first: variables?.first,
              after: pageParam.after,
            }
      );
    },
    getNextPageParam: (lastPage: Result) => {
      if (!lastPage.narratives.pageInfo.hasNextPage) return null;
      return {
        after: lastPage.narratives.pageInfo.endCursor ?? null,
        before: null,
      };
    },
    getPreviousPageParam: (firstPage: Result) => {
      if (!firstPage.narratives.pageInfo.hasPreviousPage) return null;
      return {
        before: firstPage.narratives.pageInfo.startCursor ?? null,
        after: null,
      };
    },
  });
